import { observer }              from 'mobx-react-lite';
import React                     from 'react';
import View                      from '@widesk-ui/components/View';
import Button                    from '@widesk-ui/components/Button';
import Alert                     from '@widesk-ui/components/Alert';
import Collapse                  from '@widesk-ui/components/Collapse';
import { message }               from '@widesk-ui/hooks/useMessage';
import { mercatorToLngLat }      from '@tokamap/core';
import PlanShapesModal           from '@/TokamapEditor/ux/PlanShapesModal';
import PlanLayerMapping          from '@/TokamapEditor/ux/PlanLayerMapping';
import PlanMatchField            from '@/TokamapEditor/ux/PlanMatchField';
import PlanGlyphLabelling        from '@/TokamapEditor/ux/PlanGlyphLabelling';
import PlanAnalysisRegion        from '@/TokamapEditor/ux/PlanAnalysisRegion';
import { applyArbitrations }     from '@/TokamapEditor/helpers/planDoubts';
import { doubtKey }              from '@/TokamapEditor/helpers/planDoubts';
import { nextUnsettled }         from '@/TokamapEditor/helpers/planDoubts';
import { remainingCount }        from '@/TokamapEditor/helpers/planDoubts';
import type { PlanArbitration }  from '@/TokamapEditor/helpers/planDoubts';
import type { PlanArbitrations } from '@/TokamapEditor/helpers/planDoubts';
import type { PlanCorrections }  from '@/TokamapEditor/helpers/planDoubts';
import { buildCategories }       from '@/TokamapEditor/helpers/planLayerMapping';
import { readyCategories }       from '@/TokamapEditor/helpers/planLayerMapping';
import { toMappingPayload }      from '@/TokamapEditor/helpers/planLayerMapping';
import type { PlanCategory }     from '@/TokamapEditor/helpers/planLayerMapping';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import leftSidebarStore          from '@/TokamapEditor/stores/leftSidebarStore';
import { computeFallbackPlacement } from '@/TokamapEditor/helpers/planBackground';
import { computePlacementFromShape } from '@/TokamapEditor/helpers/planBackground';
import { drawBackground }        from '@/TokamapEditor/helpers/planBackground';
import { findBackground }        from '@/TokamapEditor/helpers/planBackground';
import { layerPreviewShapeId }   from '@/TokamapEditor/helpers/planBackground';
import { placeBackground }       from '@/TokamapEditor/helpers/planBackground';
import { removeBackground }      from '@/TokamapEditor/helpers/planBackground';
import { isShapeEnabled }        from '@/TokamapEditor/helpers/planShapes';
import { needsLabelling }        from '@/TokamapEditor/helpers/planGlyphs';
import { toGlyphEntries }        from '@/TokamapEditor/helpers/planGlyphs';
import { analysePlan }           from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import { createPlanImport }      from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import { fetchPlanMatchPreview } from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import { PLAN_FILE_ACCEPT }      from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import { savePlanGlyphs }        from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import { fetchPlanLayerPreview } from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import { fetchPlanSource }       from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import { previewKey }            from '@/TokamapEditor/helpers/planLayerMapping';
import { toggleAll }             from '@/TokamapEditor/helpers/planLayerMapping';
import { applyPlaceTypeToSelection as applyPlaceType } from '@/TokamapEditor/helpers/planLayerMapping';
import { buildDecor }            from '@/TokamapEditor/helpers/planDecor';
import { setDecorStyle }         from '@/TokamapEditor/helpers/planDecor';
import { toDecorPayload }        from '@/TokamapEditor/helpers/planDecor';
import type { PlanDecor }        from '@/TokamapEditor/helpers/planDecor';
import PlanDecorLayers           from '@/TokamapEditor/ux/PlanDecorLayers';
import { saveDxfMapping }        from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import { previewDxf }            from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import { generateDxf }           from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import { fetchImportStatus }     from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import { PLAN_ANALYSIS_STEP_LABELS } from '@/MultiUserEditor/client/helpers/client/dxf/planAnalysisWait';
import type { PlanAnalysisProgress } from '@/MultiUserEditor/client/helpers/client/dxf/planAnalysisWait';
import type { DxfPlacement }     from '@models/map/dxfTypes';
import type { DxfPoi }           from '@models/map/dxfTypes';
import type { PlanAnalysisResult } from '@models/map/dxfTypes';
import type { PlanDoubt }        from '@models/map/dxfTypes';
import type { PlanGlyphScope }   from '@models/map/dxfTypes';
import type { PlanImportTypeReference } from '@models/map/dxfTypes';
import type { PlanMatchCandidate } from '@models/map/dxfTypes';
import type { PlanRegion }       from '@models/map/dxfTypes';
import type { PlanShape }        from '@models/map/dxfTypes';
import type { PlanSourceResult } from '@models/map/dxfTypes';

/**
 * Panneau d'import de PLAN (DXF, SVG, PDF ou image) — **le parcours en un écran**
 * (`docs/PLAN_PARCOURS_IMPORT.md`).
 *
 * ## Ce qui a changé, et pourquoi
 *
 * Le panneau enchaînait cinq écrans — fichier, zone d'analyse, calques, glyphes, placement —
 * dont quatre demandaient de **configurer avant de voir**. Or le serveur savait déjà proposer
 * la quasi-totalité de ce qu'ils demandaient : la zone utile, le rôle de chaque calque, les
 * formes importables, les numéros, le placement. Ce qui manquait n'était pas de la détection,
 * c'était un parcours qui **montre le résultat d'abord et ne fait arbitrer que les doutes**.
 *
 * Quatre étapes désormais :
 *
 *   1. **le plan** — un fichier, un bouton. Le serveur n'en rend que l'APERÇU
 *      (`POST /plan/source`), qui ne coûte qu'un rendu ;
 *   2. **la zone d'analyse** — sur cet aperçu, avant toute détection. Facultative : « Analyser
 *      tout le plan » est à un clic, et c'est le cas nominal ;
 *   3. **la vérification** — une lecture. `POST /plan/analysis` a tout rendu d'un coup. Le tri
 *      des formes se fait dans une modale posée sur le plan lui-même
 *      ({@link PlanShapesModal}), et les réglages restent repliés : on ne les ouvre que pour
 *      corriger. Rien n'est écrit ;
 *   4. **la génération** — le fond est posé sur la carte, on l'ajuste et on génère.
 *
 * Sur un plan sans doute, l'étape 3 se traverse sans un clic.
 *
 * ## Pourquoi la zone AVANT l'analyse
 *
 * Elle vivait en réglage replié de l'étape de vérification, c'est-à-dire APRÈS coup : sur un
 * plan à cartouche ou à plusieurs halls, l'opérateur lisait donc d'abord une détection faussée
 * — faux calques, aires médianes déformées — puis relançait tout, en perdant au passage les
 * arbitrages déjà rendus (les rangs des formes ne survivent pas à un recadrage). La zone est un
 * réglage de l'analyse : sa place est avant elle. Le panneau replié reste, pour CORRIGER un
 * cadrage sans repartir du fichier.
 *
 * ## Le tri ne se fait plus SUR la carte
 *
 * L'aperçu des formes vivait comme des formes d'éditeur posées sur la carte. Trois raisons de
 * l'avoir déplacé dans une modale (voir {@link PlanShapesModal}) : le clic y était impossible
 * (l'éditeur consomme `shape:click` pour sa propre sélection, d'où le verrouillage des formes
 * d'aperçu), le plan était posé à un endroit et une échelle arbitraires — le placement ne se
 * joue qu'à l'étape 3 —, et il fallait poser puis repeindre des centaines de formes d'éditeur
 * pour un aperçu qu'on regarde deux minutes.
 *
 * La carte garde ce qui la concerne : le FOND de l'étape 3, une forme-image déplaçable, jamais
 * enregistrée côté API et retirée dès la génération terminée ou à la fermeture du panneau.
 */

/**
 * Reprise d'un import DÉJÀ CRÉÉ, au lieu du dépôt de fichier.
 *
 * C'est la couture entre les deux entrées du parcours : `PlanZoneSidebar` capture une zone de la
 * carte, crée l'import, puis passe la main ici — la vérification et la génération sont les mêmes,
 * les réécrire en donnerait deux versions à maintenir.
 *
 * `capture: true` dit que le placement est DÉJÀ déduit côté serveur (voir `CapturePlacement`) : ni
 * fond à poser sur la carte, ni descripteur à envoyer à la génération.
 */
export type PlanImportAdoption = {
	importId: number;
	typeReference: PlanImportTypeReference;
	capture?: boolean;
	/** Le fichier déposé : un plan image s'affiche depuis lui, le serveur n'en rend que le repère. */
	file?: File;
};

const PlanImportSidebar = observer((props: { adopt?: PlanImportAdoption }) => {
	const projectUrn = currentProjectEditorStore.get?.project?.urn;

	const [file, setFile] = React.useState<File | null>(null);
	const [busy, setBusy] = React.useState(false);
	const [importId, setImportId] = React.useState<number | null>(null);
	const [typeReference, setTypeReference] = React.useState<PlanImportTypeReference | ''>('');
	// Import né d'une CAPTURE de zone et non d'un fichier : le placement est déduit côté serveur,
	// l'étape « poser le fond sur la carte » n'a plus lieu d'être.
	const [capture, setCapture] = React.useState(false);
	// L'APERÇU du plan, rendu dès l'ouverture du fichier — avant toute détection. C'est le
	// support de l'étape 2 : on ne peut pas désigner une zone sans voir le plan.
	const [source, setSource] = React.useState<PlanSourceResult | null>(null);

	// Un plan image arrive SANS image : le navigateur affiche le fichier d'origine, en pleine
	// qualité, dans le repère que le serveur a rendu (mêmes proportions).
	const localUrl = React.useRef<string | null>(null);
	React.useEffect(() => () => {
		if (localUrl.current) URL.revokeObjectURL(localUrl.current);
	}, []);
	const withLocalImage = (preview: PlanSourceResult, local: File | null | undefined): PlanSourceResult => {
		if (preview.imageDataUri || !local) return preview;
		if (localUrl.current) URL.revokeObjectURL(localUrl.current);
		localUrl.current = URL.createObjectURL(local);

		return { ...preview, imageDataUri: localUrl.current };
	};
	// L'analyse COMPLÈTE du plan : aperçu, calques, formes, numéros, doutes, profil. `null`
	// tant que l'analyse n'a pas été lancée. C'est la seule source de l'étape 3.
	const [analysis, setAnalysis] = React.useState<PlanAnalysisResult | null>(null);
	/**
	 * L'étape en cours, telle que le serveur l'annonce pendant qu'il travaille.
	 *
	 * L'analyse est asynchrone : sans ce fil, l'écran n'aurait qu'un sablier à montrer sur un
	 * plan qui met vingt secondes. Null dès que l'attente est finie — ou si le hub Mercure est
	 * absent, auquel cas l'analyse aboutit quand même, en silence.
	 */
	const [progress, setProgress] = React.useState<PlanAnalysisProgress | null>(null);
	// Zone d'analyse en cours de tracé, `null` = tout le plan (le cas nominal).
	const [region, setRegion] = React.useState<PlanRegion | null>(null);
	/**
	 * Ce que la détection du NAVIGATEUR a produit au dernier tour, ou null.
	 *
	 * Purement informatif : la voie est expérimentale, et l'opérateur doit pouvoir lire d'un
	 * coup d'œil si les formes qu'il regarde viennent de son navigateur ou du serveur. Sans
	 * ça, un réglage qui échoue en silence — fichier absent, worker tué — passerait pour un
	 * réglage qui n'a rien changé.
	 */
	const [categories, setCategories] = React.useState<PlanCategory[]>([]);
	const [decor, setDecor] = React.useState<PlanDecor>({});
	// Décisions de l'opérateur, par forme. Rien n'est écrit avant l'étape 3 : ces deux tables
	// deviennent des `shapes` / `forcedShapes` / `numbers` au moment du mapping.
	const [arbitrations, setArbitrations] = React.useState<PlanArbitrations>({});
	const [corrections, setCorrections] = React.useState<PlanCorrections>({});
	const [doubtIndex, setDoubtIndex] = React.useState(0);
	const [sorting, setSorting] = React.useState(false);
	// Champs de rapprochement mesurés par le serveur, `null` tant qu'on ne les a pas demandés.
	const [matchCandidates, setMatchCandidates] = React.useState<PlanMatchCandidate[] | null>(null);
	const [matchNumbers, setMatchNumbers] = React.useState(0);
	// Étiquetage de police : plus une ÉTAPE, mais un réglage replié. La correction ordinaire
	// d'un numéro se fait sur la carte de doute, qui montre la forme qui le porte.
	const [glyphValues, setGlyphValues] = React.useState<Record<string, string>>({});
	const [glyphScope, setGlyphScope] = React.useState<PlanGlyphScope>('project');
	const [previewedLayer, setPreviewedLayer] = React.useState<string | null>(null);
	const [preview, setPreview] = React.useState<PlanPreview | null>(null);
	// Forme-image de fond de l'étape 3 — `null` avant l'aperçu, ou après suppression manuelle.
	const [shapeId, setShapeId] = React.useState<string | null>(null);
	const [placement, setPlacement] = React.useState<DxfPlacement | null>(null);
	const [error, setError] = React.useState<string | null>(null);

	// Le fond ne survit pas au panneau : le laisser derrière soi poserait une forme fantôme,
	// jamais enregistrée, que plus rien ne piloterait (l'id vit dans cet état React).
	const shapeIdRef = React.useRef<string | null>(null);
	shapeIdRef.current = shapeId;
	const layerPreviewIdRef = React.useRef<string | null>(null);

	React.useEffect(() => () => {
		const instance = currentEditorStore.get;
		if (!instance) return;
		if (shapeIdRef.current) removeBackground(instance, shapeIdRef.current);
		if (layerPreviewIdRef.current) removeBackground(instance, layerPreviewIdRef.current);
	}, []);

	const doubts = analysis?.doubts ?? [];
	const remaining = remainingCount(doubts, arbitrations);

	/**
	 * **Les catégories telles qu'elles PARTIRONT au serveur** — décisions de l'opérateur
	 * comprises.
	 *
	 * C'est cette liste-là qu'on affiche, jamais l'état brut. Sans ça, écarter une forme la
	 * laissait cochée dans la liste des calques : l'écran promettait un import qui n'aurait pas
	 * lieu, et l'opérateur n'avait aucun moyen de savoir ce qui serait créé.
	 *
	 * `categories` reste la source ÉDITABLE (les cases, les types de POI) ; les clés ne bougent
	 * pas, donc `updateCategory` continue de viser la bonne ligne.
	 */
	const effective = React.useMemo(() => applyArbitrations({
		categories,
		shapes: analysis?.shapes ?? [],
		arbitrations,
		corrections,
	}), [categories, analysis, arbitrations, corrections]);

	/** La forme sera-t-elle importée en l'état ? La règle du serveur, appliquée à la liste. */
	const isEnabled = React.useCallback(
		(shape: PlanShape) => isShapeEnabled(effective, shape.layer, shape.index),
		[effective],
	);

	// Le fond se manipule SUR la carte : l'overlay sombre du tiroir (qui capte aussi les clics)
	// doit disparaître pendant le placement — y compris quand aucun fond n'est posé (l'opérateur
	// est alors justement en train d'en tracer un à la main) — et pendant l'aperçu d'un calque.
	React.useEffect(() => {
		leftSidebarStore.setOption('overlay', !preview && !previewedLayer);
	}, [preview, previewedLayer]);

	// Suit les déplacements / redimensionnements / rotations du fond, et sa disparition
	// éventuelle (Suppr) : `getAllShapes` n'est pas observable, seul ce listener l'est.
	React.useEffect(() => {
		const instance = currentEditorStore.get;
		if (!instance || !shapeId || !preview) return;

		return instance.shapeStore.onChange(() => {
			const shape = findBackground(instance, shapeId);
			if (!shape) {
				setShapeId(null);
				setPlacement(null);

				return;
			}
			setPlacement(computePlacementFromShape(shape, preview.width));
		});
	}, [shapeId, preview]);

	/** Retire l'aperçu de calque posé sur la carte, s'il y en a un. */
	const clearLayerPreview = () => {
		const instance = currentEditorStore.get;
		if (instance && layerPreviewIdRef.current) removeBackground(instance, layerPreviewIdRef.current);
		layerPreviewIdRef.current = null;
		setPreviewedLayer(null);
	};

	/** Traduit une erreur d'API en message lisible : le back renvoie des 422 explicites. */
	const fail = (e: unknown, fallback: string) => {
		const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
		const text = detail || (e as Error)?.message || fallback;
		setError(text);
		message.error(text);
	};

	/**
	 * Charge une analyse et remet l'écran 2 dans son état de départ.
	 *
	 * Les décisions sont REMISES À ZÉRO : elles portent sur des rangs, et une analyse relancée
	 * avec une autre zone ne garantit plus que le rang 47 désigne la même forme. Les rejouer
	 * silencieusement serait exactement l'erreur invisible que le rang a été conçu pour
	 * éliminer.
	 */
	const loadAnalysis = async (
		id: number,
		options: { region?: PlanRegion | null; imageDataUri?: string } = {},
	) => {
		// L'aperçu ne revient plus avec l'analyse (le serveur ne le range pas, ~400 ko) : on lui
		// repasse celui qu'on a déjà, sans quoi le plan disparaîtrait de l'écran au moment même
		// où il faut le regarder.
		const image = options.imageDataUri ?? source?.imageDataUri;

		setProgress(null);
		const result = await analysePlan(id, { ...options, imageDataUri: image, onProgress: setProgress });
		setProgress(null);

		if (!result.layers.length) {
			throw new Error('Aucun calque exploitable dans ce plan.');
		}

		setAnalysis(result);
		// Forme fonctionnelle, et pas `setSource(result.source)` : sur un chemin où l'image n'a
		// pas pu être repassée, c'est le dernier filet avant un aperçu vide.
		setSource(prev => ({ ...result.source, imageDataUri: result.source.imageDataUri || prev?.imageDataUri || '' }));
		setRegion(result.source.analysisRegion);
		// Les formes sont passées à `buildCategories` : une ligne dont aucune forme n'est
		// importable sort DÉCOCHÉE, au lieu de promettre un POI qui ne sortira pas.
		setCategories(buildCategories(result.layers, result.suggestedMapping, result.shapes));
		setDecor(buildDecor(result.suggestedDecor));
		setArbitrations({});
		setCorrections({});
		setDoubtIndex(0);
		// Les candidats mesurés valaient pour l'analyse précédente : les garder ferait choisir
		// un champ de rapprochement sur des chiffres périmés.
		setMatchCandidates(null);

		return result;
	};

	/**
	 * Étape 1 : dépose le fichier et en rend l'APERÇU — rien de plus.
	 *
	 * L'analyse ne part pas d'ici : elle a un réglage, la zone, et ce réglage se prend sur
	 * l'image. Analyser d'abord ferait payer une détection complète pour la jeter aussitôt
	 * qu'un cartouche traîne dans un coin.
	 */
	const openPlan = async () => {
		if (!file) return message.warning('Sélectionnez un plan : .dxf, .svg, .pdf, .png, .jpg ou .webp.');
		if (!projectUrn) return message.error('Projet introuvable.');

		setBusy(true);
		setError(null);
		try {
			const mapUrn = currentProjectEditorStore.get.currentMap.urn;
			const created = await createPlanImport({ planFile: file, mapUrn, projectUrn });
			setImportId(created.importId);
			setTypeReference(created.typeReference);

			const preview = withLocalImage(await fetchPlanSource(created.importId), file);
			setSource(preview);
			// Un import repris garde sa zone : la reprendre telle quelle évite de la retracer.
			setRegion(preview.analysisRegion);
		} catch (e) {
			fail(e, 'Échec de la lecture du plan.');
		} finally {
			setBusy(false);
		}
	};

	/**
	 * Lance l'analyse — la PREMIÈRE, depuis l'étape de la zone, comme celles qui la rejouent
	 * après une correction de zone ou un dictionnaire de glyphes.
	 *
	 * `region` n'est transmise que lorsqu'on la CHANGE : sans le champ, le serveur conserve la
	 * zone en place, et un simple rafraîchissement ne doit pas effacer le cadre en cours.
	 */
	const reanalyse = async (options: { region?: PlanRegion | null } = {}) => {
		if (!importId) return;

		setBusy(true);
		setError(null);
		try {
			await loadAnalysis(importId, options);
		} catch (e) {
			fail(e, 'Échec de l’analyse du plan.');
		} finally {
			setBusy(false);
		}
	};

	/**
	 * Reprise d'un import déjà créé ({@link PlanImportAdoption}) : on saute le dépôt de fichier et
	 * l'étape de zone, et on entre directement dans la vérification.
	 *
	 * Une seule fois, au montage : le panneau est remonté à chaque `openPlanImport`, et l'adoption
	 * n'est qu'un point de départ — la relancer écraserait les arbitrages en cours.
	 */
	const adopted = React.useRef(false);
	React.useEffect(() => {
		const adopt = props.adopt;
		if (!adopt || adopted.current) return;
		adopted.current = true;

		setImportId(adopt.importId);
		setTypeReference(adopt.typeReference);
		setCapture(!!adopt.capture);

		setBusy(true);
		setError(null);
		// L'aperçu AVANT l'analyse : ce parcours saute l'étape 1, et l'analyse ne rend plus
		// l'image. Sans cette lecture, la vérification s'ouvrirait sur un plan invisible.
		fetchPlanSource(adopt.importId)
			.then(fetched => {
				const preview = withLocalImage(fetched, adopt.file);
				setSource(preview);

				return loadAnalysis(adopt.importId, { imageDataUri: preview.imageDataUri });
			})
			.catch(e => fail(e, 'Échec de l’analyse de la zone.'))
			.finally(() => setBusy(false));
	}, [props.adopt]);

	/** Applique une modification ponctuelle à une catégorie (coche, type de POI, calque numéros). */
	const updateCategory = (key: string, patch: Partial<PlanCategory>) => {
		setCategories(prev => prev.map(category => (category.key === key ? { ...category, ...patch } : category)));
	};

	const toggleAllCategories = (enabled: boolean) => setCategories(prev => toggleAll(prev, enabled));

	const applyPlaceTypeToSelection = (placeTypeUrn: string) =>
		setCategories(prev => applyPlaceType(prev, placeTypeUrn));

	/**
	 * Enregistre la décision prise sur UNE forme — par un clic sur le plan, ou par la file.
	 *
	 * Les deux gestes disent la même chose et écrivent au même endroit. La file n'avance que si
	 * la forme arbitrée est bien celle qu'elle affiche : un clic ailleurs sur le plan ne doit
	 * pas faire défiler la carte que l'opérateur est en train de lire.
	 */
	const arbitrate = (shape: Pick<PlanShape, 'layer' | 'index'>, decision: PlanArbitration) => {
		const key = doubtKey(shape);
		const next = { ...arbitrations, [key]: decision };
		setArbitrations(next);

		const current = doubts[doubtIndex];
		if (!current || doubtKey(current) !== key) return;

		const following = nextUnsettled(doubts, next, doubtIndex);
		// Plus rien à regarder : on RESTE sur la carte courante plutôt que de sauter au début.
		// Le compteur dit « toutes regardées », et l'opérateur peut revenir en arrière.
		if (null !== following) setDoubtIndex(following);
	};

	const correct = (doubt: PlanDoubt, value: string) =>
		setCorrections(prev => ({ ...prev, [doubtKey(doubt)]: value }));

	/** Le corps de `/plan/mapping` — exactement ce que la liste affiche. */
	const mappingPayload = () => toMappingPayload(effective);
	const decorPayload = () => toDecorPayload(decor, effective);

	/**
	 * Mesure ce que chaque champ de rapprochement donnerait.
	 *
	 * Exige un `layerMapping` déjà enregistré côté serveur — c'est lui qui désigne les calques
	 * de numéros. On l'enregistre donc d'abord, ce qui est sans effet de bord : le mapping est
	 * de toute façon ce qu'on s'apprête à valider.
	 */
	const measureMatchFields = async () => {
		if (!importId) return;

		const { layerMapping, categoryPlaceTypeMapping, matchFieldMapping } = mappingPayload();
		if (!layerMapping.length) {
			return message.warning('Cochez au moins une ligne et donnez-lui un type de POI.');
		}

		setBusy(true);
		setError(null);
		try {
			await saveDxfMapping(importId, layerMapping, categoryPlaceTypeMapping, matchFieldMapping, undefined, decorPayload());
			const result = await fetchPlanMatchPreview(importId, categoryPlaceTypeMapping);
			setMatchCandidates(result.candidates);
			setMatchNumbers(result.numbers);

			if (!result.candidates.length) {
				message.info('Aucun POI de ces types sur la carte : tout sera créé.');
			}
		} catch (e) {
			fail(e, 'Échec de la mesure du rapprochement.');
		} finally {
			setBusy(false);
		}
	};

	/**
	 * **Bouton œil : montre sur la carte les formes de CETTE ligne.**
	 *
	 * Le calque est rendu SEUL, en image, au centre de la vue. Sa position n'a aucun sens à
	 * cette étape — le placement se joue plus tard —, seule sa forme est à juger. Pour désigner
	 * une forme précise, c'est la modale de tri qu'il faut, pas la carte.
	 */
	const togglePreviewLayer = async (category: Pick<PlanCategory, 'baseLayer' | 'shapes'>) => {
		const key = previewKey(category);
		const instance = currentEditorStore.get;
		if (!instance || !importId) return;

		if (previewedLayer === key) return clearLayerPreview();

		setBusy(true);
		setError(null);
		try {
			// Une catégorie qui ne désigne QU'UNE forme se prévisualise seule : sur un plan
			// raster, son calque en porte des dizaines, et les allumer toutes ne dit pas
			// laquelle on coche.
			const { svgDataUri, width, height } = await fetchPlanLayerPreview(
				importId,
				category.baseLayer,
				category.shapes?.[0] ?? null,
			);
			const target = computeFallbackPlacement(instance, width, height);
			if (!target) throw new Error('Carte non prête : impossible de poser l’aperçu.');

			clearLayerPreview();
			layerPreviewIdRef.current = placeBackground({
				instance,
				importId,
				src: svgDataUri,
				widthPx: width,
				heightPx: height,
				placement: target,
				shapeId: layerPreviewShapeId(importId),
				edit: false,
			});
			setPreviewedLayer(key);
		} catch (e) {
			fail(e, 'Échec de l’aperçu du calque.');
		} finally {
			setBusy(false);
		}
	};

	/** Enregistre l'étiquetage de police saisi, puis relance l'analyse pour en voir l'effet. */
	const saveGlyphs = async () => {
		if (!importId) return;

		const entries = toGlyphEntries(glyphValues);
		if (!Object.keys(entries).length) return message.info('Aucun caractère saisi.');

		setBusy(true);
		setError(null);
		try {
			// Le rapport RENVOYÉ est la seule source de vérité : le serveur ignore silencieusement
			// une entrée invalide plutôt que de tout rejeter.
			const refreshed = await savePlanGlyphs(importId, entries, glyphScope);
			message.success(`${refreshed.readableLabels}/${refreshed.totalLabels} libellés lisibles.`);
			setGlyphValues({});
			// Des numéros nouvellement lisibles changent la file des doutes : la relire est le
			// seul moyen que l'écran ne mente pas sur ce qui reste à corriger.
			await loadAnalysis(importId);
		} catch (e) {
			fail(e, 'Échec de l’enregistrement du dictionnaire.');
		} finally {
			setBusy(false);
		}
	};

	/**
	 * Étape 2 → 3 : enregistre le mapping (décisions comprises) puis pose le fond.
	 *
	 * L'étape d'étiquetage de glyphes a disparu du parcours : ce qu'elle réglait se corrige
	 * désormais sur la carte de doute, qui montre la forme portant le numéro illisible. Le
	 * dictionnaire reste accessible, replié, pour l'opérateur qui veut nommer une police une
	 * fois pour toutes ses prochaines importations.
	 */
	const validateAndPreview = async () => {
		if (!importId) return;

		const { layerMapping, categoryPlaceTypeMapping, matchFieldMapping } = mappingPayload();
		if (!layerMapping.length) {
			return message.warning('Cochez au moins un calque et associez-lui un type de POI.');
		}

		// L'aperçu de calque est un échafaudage : le laisser en place ferait cohabiter deux
		// plans sur la carte dès que le fond arrive.
		clearLayerPreview();
		setSorting(false);

		setBusy(true);
		setError(null);
		try {
			// Zone volontairement OMISE : elle a été posée à l'analyse, et le serveur distingue
			// « champ absent » (conserver) de « rectangle vide » (effacer).
			await saveDxfMapping(importId, layerMapping, categoryPlaceTypeMapping, matchFieldMapping, undefined, decorPayload());
			await runPreview(importId);
		} catch (e) {
			fail(e, 'Échec de l’aperçu.');
		} finally {
			setBusy(false);
		}
	};

	/** Aperçu du fond + POI, dernière étape avant le placement sur la carte. */
	const runPreview = async (id: number) => {
		const result = await previewDxf(id);
		const next: PlanPreview = {
			svg: result.svgDataUri,
			width: result.width,
			height: result.height,
			pois: result.pois,
			isGeoreferenced: result.isGeoreferenced,
			suggestedPlacement: result.suggestedPlacement,
		};

		setPreview(next);
		// Capture : le placement est déjà déduit côté serveur (`CapturePlacement`) — poser un fond
		// à caler n'aurait rien à régler, et inviterait à déplacer quelque chose qui n'est plus lu.
		if (!capture) putBackgroundOnMap(next);
	};

	/**
	 * Pose le fond sur la carte : depuis le géoréférencement du plan quand il en a un, sinon
	 * centré sur la vue courante. Dans les deux cas l'opérateur peut ensuite l'ajuster.
	 */
	const putBackgroundOnMap = (current: PlanPreview) => {
		const instance = currentEditorStore.get;
		if (!instance || !importId) return;

		const target = (current.isGeoreferenced && current.suggestedPlacement)
			? current.suggestedPlacement
			: computeFallbackPlacement(instance, current.width, current.height);

		if (!target) {
			setError('La vue de la carte n’est pas exploitable : placez le fond à la main.');

			return;
		}

		const id = placeBackground({
			instance,
			importId,
			src: current.svg,
			widthPx: current.width,
			heightPx: current.height,
			placement: target,
		});

		setShapeId(id);
		setPlacement(target);
		message.info(current.isGeoreferenced
			? 'Fond pré-placé depuis le géoréférencement du plan. Ajustez-le si besoin.'
			: 'Fond pré-placé au centre de la vue. Déplacez-le, redimensionnez-le, pivotez-le.');
	};

	/** Placement manuel : l'opérateur trace lui-même la boîte du fond, coin à coin. */
	const drawBackgroundByHand = async () => {
		const instance = currentEditorStore.get;
		if (!instance || !preview || !importId) return;

		if (shapeId) removeBackground(instance, shapeId);
		setShapeId(null);
		setPlacement(null);

		const drawnId = await drawBackground({
			instance,
			importId,
			src: preview.svg,
			widthPx: preview.width,
			heightPx: preview.height,
		});

		if (!drawnId) return message.warning('Placement annulé : le fond n’est plus sur la carte.');

		const drawn = findBackground(instance, drawnId);
		setShapeId(drawnId);
		setPlacement(drawn ? computePlacementFromShape(drawn, preview.width) : null);
	};

	const generate = async () => {
		const instance = currentEditorStore.get;
		if (!instance || !importId || !preview) return;

		// Capture : rien à relire sur la carte, et rien à en retirer ensuite — le serveur a déduit
		// le placement de la zone entourée en gelant sa transform. Le champ est OMIS du corps, pas
		// envoyé vide : côté serveur, « absent » veut dire « garde celui que tu as ».
		const currentShapeId = capture ? null : shapeId;
		let current: DxfPlacement | null = null;

		if (!capture) {
			const shape = currentShapeId ? findBackground(instance, currentShapeId) : null;
			if (!shape || !currentShapeId) return message.error('Fond introuvable sur la carte : reposez-le avant de générer.');

			current = computePlacementFromShape(shape, preview.width);
			if (!current) return message.error('Le fond n’est plus un rectangle exploitable : reposez-le.');
		}

		setBusy(true);
		setError(null);
		try {
			await generateDxf(importId, current, []);

			const status = await fetchImportStatus(importId);
			if (status !== 'done') throw new Error('L’import s’est terminé en erreur.');

			// Le fond n'était qu'un repère de placement : il disparaît avec la génération. (Aucun
			// n'a été posé en mode capture.)
			if (currentShapeId) removeBackground(instance, currentShapeId);
			setShapeId(null);
			setPlacement(null);

			// Le récapitulatif dit ce qui s'est passé, **y compris sur quel champ les POI ont
			// été rapprochés** : c'est ce qui distingue une mise à jour d'une création en
			// doublon, et c'est ce que personne ne voyait jusqu'ici.
			message.success(
				`${preview.pois.length} POI générés (${describeMatching(matchCandidates, effective)}) — `
				+ 'rechargez la page pour les voir sur la carte.',
			);

			resetPanel();
		} catch (e) {
			fail(e, 'Échec de la génération.');
		} finally {
			setBusy(false);
		}
	};

	/** Import consommé : on revient au dépôt de fichier. */
	const resetPanel = () => {
		// L'aperçu de calque est un ÉCHAFAUDAGE posé sans `layer` : l'adaptateur peint la bande
		// « sans calque » AU-DESSUS de toutes les bandes numérotées (voir `sortedLayerBands`). Le
		// laisser derrière soi cache donc exactement ce qu'on vient de créer — les zones générées,
		// qui portent un calque (`resolveZoneLayer`, max + 1). L'oubli ne se voyait pas dans le
		// parcours par fichier, où le fond disparaît de toute façon à la génération.
		clearLayerPreview();
		setPreview(null);
		setSource(null);
		setAnalysis(null);
		setProgress(null);
		setCategories([]);
		setDecor({});
		setArbitrations({});
		setCorrections({});
		setDoubtIndex(0);
		setSorting(false);
		setMatchCandidates(null);
		setGlyphValues({});
		setImportId(null);
		setTypeReference('');
		setCapture(false);
		setFile(null);
		setRegion(null);
	};

	const named = preview?.pois.filter(poi => poi.label).length ?? 0;
	const glyphs = analysis?.glyphs ?? null;
	// Ce qui sera RÉELLEMENT créé : la promesse de l'écran, et le seul chiffre qui compte.
	const willImport = (analysis?.shapes ?? []).filter(isEnabled).length;

	return (
		<View padding={16} gap={16}>
			<Alert
				type="info"
				showIcon
				message="Import de plan"
				description={
					'DXF, SVG, PDF ou image. Vous délimitez la zone à analyser, le plan est analysé en '
					+ 'une fois, et vous n’arbitrez que ce qui fait doute — puis vous placez le fond et '
					+ 'générez.'
				}
			/>

			{error && <Alert type="error" showIcon message="Refus du serveur" description={error} />}

			{/* ── Étape 1 : le plan ──────────────────────────────────────────────── */}
			{!source && (
				<View gap={8}>
					<View size={14} bold>1. Le plan</View>
					<input
						type="file"
						accept={PLAN_FILE_ACCEPT}
						onChange={e => setFile(e.target.files?.[0] ?? null)}
					/>
					<View size={12} color="#999">
						Plan technique (.dxf), plan public (.svg, .pdf) ou plan qu’on n’a plus qu’en
						image (.png, .jpg, .webp) — y compris un PDF scanné.
					</View>
					<Button type="primary" loading={busy} disabled={!file} onClick={openPlan}>
						Ouvrir le plan
					</Button>
				</View>
			)}

			{/* ── Étape 2 : la zone d'analyse, AVANT toute détection ─────────────── */}
			{source && !analysis && (
				<View gap={10}>
					<View size={14} bold>2. Zone d’analyse</View>
					<View size={12}>Import #{importId} — format « {typeReference} »</View>

					<PlanAnalysisRegion
						source={source}
						region={region}
						disabled={busy}
						openOnMount
						onChange={setRegion}
					/>

					<View size={12} color="#999">
						Facultatif. Entourez la partie du plan à détecter si le document porte un
						cartouche, une légende, des marges — ou plusieurs halls sur la même planche :
						ces éléments faussent la détection PARTOUT, les couleurs comme les tailles de
						référence. Sinon, analysez tout le plan.
					</View>

					<Button type="primary" loading={busy} onClick={() => void reanalyse({ region })}>
						{region ? 'Analyser cette zone' : 'Analyser tout le plan'}
					</Button>

					{busy && progress && (
						<View size={12} style={{ opacity: 0.7 }}>
							{PLAN_ANALYSIS_STEP_LABELS[progress.step] ?? 'Analyse en cours'} —{' '}
							étape {progress.current} sur {progress.total}
						</View>
					)}

					<Button size="small" disabled={busy} onClick={resetPanel}>
						Changer de plan
					</Button>
				</View>
			)}

			{/* ── Étape 3 : vérification ─────────────────────────────────────────── */}
			{analysis && !preview && projectUrn && (
				<View gap={10}>
					<View size={14} bold>3. Vérification</View>
					<View size={12}>Import #{importId} — format « {typeReference} »</View>

					<Alert
						type={remaining ? 'warning' : 'success'}
						showIcon
						message={`${willImport} POI seront créés${remaining ? ` · ${remaining} forme(s) à vérifier` : ''}`}
						description={
							remaining
								? 'Ouvrez le tri pour voir les formes sur le plan et arbitrer ce qui fait doute.'
								: 'Aucune forme ne demande d’arbitrage. Ouvrez le tri si vous voulez vérifier de visu.'
						}
					/>

					<Button type={remaining ? 'primary' : 'default'} onClick={() => setSorting(true)}>
						Trier les formes ({analysis.shapes.length} sur le plan)
					</Button>

					{/* Tout ce qui est replié est DÉJÀ décidé : on ne l'ouvre que pour corriger. */}
					<Collapse
						size="small"
						items={[
							{
								key: 'mapping',
								label: `Association des calques — ${readyCategories(effective).length} ligne(s) prête(s)`,
								children: (
									<PlanLayerMapping
										layers={analysis.layers}
										categories={effective}
										shapes={analysis.shapes}
										readsText={analysis.source.readsText !== false}
										projectUrn={projectUrn}
										previewedLayer={previewedLayer}
										onChange={updateCategory}
										onToggleAll={toggleAllCategories}
										onApplyPlaceType={applyPlaceTypeToSelection}
										onPreviewLayer={togglePreviewLayer}
									/>
								),
							},
							{
								key: 'decor',
								label: `Décor du plan — ${toDecorPayload(decor, effective).length} calque(s)`,
								children: (
									<PlanDecorLayers
										layers={analysis.layers}
										categories={effective}
										decor={decor}
										previewedLayer={previewedLayer}
										onChange={(layer, style) => setDecor(prev => setDecorStyle(prev, layer, style))}
										onPreviewLayer={layer => void togglePreviewLayer({ baseLayer: layer, shapes: null })}
									/>
								),
							},
							{
								key: 'match',
								label: 'Rapprochement des POI existants',
								children: (
									<PlanMatchField
										categories={effective}
										candidates={matchCandidates}
										numbers={matchNumbers}
										busy={busy}
										onMeasure={() => void measureMatchFields()}
										onChange={(key, field) => updateCategory(key, { matchField: field })}
									/>
								),
							},
							{
								key: 'region',
								label: region ? 'Corriger la zone d’analyse — restreinte' : 'Corriger la zone d’analyse — tout le plan',
								children: (
									<View gap={8}>
										<PlanAnalysisRegion
											source={analysis.source}
											region={region}
											disabled={busy}
											onChange={setRegion}
										/>
										<Button size="small" loading={busy} onClick={() => void reanalyse({ region })}>
											{region ? 'Réanalyser cette zone' : 'Réanalyser tout le plan'}
										</Button>
										<View size={11} color="#999">
											Changer la zone relance l’analyse et REMET À ZÉRO les arbitrages : les
											rangs des formes ne survivent pas à un recadrage.
										</View>
									</View>
								),
							},
							...(glyphs && needsLabelling(glyphs) ? [{
								key: 'glyphs',
								label: `Numéros lus — ${Math.round(glyphs.labelCoverage * 100)} % lisibles`,
								children: (
									<View gap={8}>
										<View size={11} color="#999">
											Corriger un numéro se fait sur la carte de doute, qui montre la forme
											qui le porte. Cet écran-ci nomme la POLICE une fois pour toutes :
											utile quand le même exposant revient chaque année.
										</View>
										<PlanGlyphLabelling
											report={glyphs}
											values={glyphValues}
											scope={glyphScope}
											disabled={busy}
											onChange={(signature, value) => setGlyphValues(prev => ({ ...prev, [signature]: value }))}
											onScopeChange={setGlyphScope}
										/>
										<Button size="small" loading={busy} onClick={() => void saveGlyphs()}>
											Enregistrer le dictionnaire et réanalyser
										</Button>
									</View>
								),
							}] : []),
						]}
					/>

					<Button type="primary" loading={busy} onClick={() => void validateAndPreview()}>
						Continuer ({readyCategories(effective).length} ligne(s) sélectionnée(s))
					</Button>

					<PlanShapesModal
						open={sorting}
						source={analysis.source}
						shapes={analysis.shapes}
						doubts={doubts}
						arbitrations={arbitrations}
						corrections={corrections}
						isEnabled={isEnabled}
						doubtIndex={doubtIndex}
						truncated={analysis.truncated}
						onGoDoubt={setDoubtIndex}
						onArbitrate={arbitrate}
						onCorrect={correct}
						onClose={() => setSorting(false)}
					/>
				</View>
			)}

			{/* ── Étape 4 : placement du fond et génération ──────────────────────── */}
			{preview && (
				<View gap={8}>
					<View size={14} bold>4. Génération</View>
					<View size={12}>
						{preview.pois.length} POI détectés, dont {named} avec un numéro.
					</View>

					<img
						src={preview.svg}
						alt="Aperçu du plan"
						style={{ width: '100%', maxHeight: 140, objectFit: 'contain', border: '1px solid #ddd', background: '#fff' }}
					/>

					{/* En mode CAPTURE il n'y a rien à placer, et c'est tout l'intérêt du parcours : la
					    zone étant déjà en coordonnées de la carte, le serveur a déduit le descripteur
					    en gelant sa transform (`CapturePlacement`). Demander un fond ici, c'était
					    réclamer le geste que ce parcours existe pour supprimer — et le bouton restait
					    désactivé pour toujours, aucun fond n'étant jamais posé dans ce mode. */}
					{capture ? (
						<Alert
							type="info"
							showIcon
							message="Placement déduit de la zone entourée"
							description="Rien à poser sur la carte : les formes retomberont là où vous avez tracé."
						/>
					) : (
						<>
							{shapeId ? (
								<Alert
									type="success"
									showIcon
									message="Fond posé sur la carte"
									description={`Glissez-le pour le déplacer, tirez ses coins pour le redimensionner (ratio verrouillé), sa poignée pour le pivoter.${
										describeGroundWidth(placement, preview.width)
									}`}
								/>
							) : (
								<Alert
									type="warning"
									showIcon
									message="Aucun fond sur la carte"
									description="Reposez-le au centre de la vue ou tracez-le à la main pour pouvoir générer."
								/>
							)}

							<View row gap={8}>
								<Button loading={busy} onClick={() => putBackgroundOnMap(preview)}>
									{shapeId ? 'Recentrer le fond' : 'Reposer le fond'}
								</Button>
								<Button loading={busy} onClick={drawBackgroundByHand}>
									Placer à la main
								</Button>
							</View>
						</>
					)}

					<Button type="primary" loading={busy} disabled={!capture && !shapeId} onClick={() => void generate()}>
						Générer les POI
					</Button>
				</View>
			)}
		</View>
	);
});

/** Aperçu renvoyé par `/plan/preview`, tel qu'exploité par ce panneau. */
type PlanPreview = {
	svg: string;
	/** Dimensions PIXEL gelées du rendu — la boîte que le placement met à l'échelle. */
	width: number;
	height: number;
	pois: DxfPoi[];
	isGeoreferenced: boolean;
	suggestedPlacement: DxfPlacement | null;
};

/**
 * Sur quel champ les POI ont été rapprochés, pour le récapitulatif de fin.
 *
 * Le dire n'est pas décoratif : c'est la seule ligne du parcours qui distingue « 141 POI mis à
 * jour » de « 141 doublons créés », et l'opérateur n'a aucun autre moyen de savoir laquelle des
 * deux vient de se produire.
 */
function describeMatching(candidates: PlanMatchCandidate[] | null, categories: PlanCategory[]): string {
	const chosen = categories.find(category => category.enabled && !!category.matchField);
	if (!chosen) return 'rapprochés sur le libellé, par défaut';

	const label = candidates?.find(candidate => candidate.field === chosen.matchField)?.label;

	return `rapprochés sur « ${label ?? chosen.matchField} »`;
}

/**
 * Largeur du plan au sol, en mètres, pour que l'opérateur puisse recouper l'échelle obtenue avec
 * une dimension qu'il connaît ("mon hall fait 200 m").
 *
 * `placement.scale` est en unités EPSG:3857 par pixel — des "mètres" étirés d'un facteur
 * 1/cos(latitude) (à Paris : ~1,5). On applique donc `cos(latitude)` pour annoncer une vraie
 * distance au sol, jamais la valeur brute du repère.
 */
function describeGroundWidth(placement: DxfPlacement | null, widthPx: number): string {
	if (!placement || !widthPx) return '';

	const [, latitude] = mercatorToLngLat(placement.center);
	const meters = placement.scale * widthPx * Math.cos((latitude * Math.PI) / 180);
	if (!Number.isFinite(meters) || meters <= 0) return '';

	return ` Largeur actuelle du plan : ~${Math.round(meters)} m.`;
}

export default PlanImportSidebar;
